import { computed, effect, inject, Injectable, signal, Signal } from '@angular/core';
import { FormArray, FormControlStatus, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import {
    DivisionBasicSettings,
    CriteriaType,
    DataMartField,
    DataMartMultipleRecommendationsTableMapResponse,
    DataMartTable,
    DivisionConfig,
    DivisionMappingSettings,
    SalesforceListFieldResponse,
    SalesforceUniqueFieldTarget,
    DefinedField,
    RequiredDivisionConfigData,
} from '@r1-compass-library/models';
import { PlaySearch } from '@r1-compass-library/models/search/play-search.model';
import { DataMartService, PlayService } from '@r1-compass-library/services';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { conditionalValidator } from '@r1-compass-library/validators';
import { signalSlice } from 'ngxtension/signal-slice';
import { formCombined } from '@r1-compass-library/utilities';
import { combineLatest, concatMap, forkJoin, from, lastValueFrom, map, Observable, of, switchMap, toArray } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { GlobalDivisionsStore } from '@r1-compass-library/store/divisions/global-divisions-store';
import { DataMartLifecyclesTableStore } from '../../lifecycle-rules/store/data-mart-lifecycles.store';
import { DivisionBasicSettingsFormValidatorsService } from './division-basic-settings-form-validators.service';
import { DivisionSettingsFormSliceState, FormLifecycleService } from '../form-lifecycle.service';
import { AdditionalFieldForm, BasicSettingsFormValue, DivisionBasicSettingsForm, PicklistForm } from './form.model';

interface DivisionBasicSettingsFormSliceState extends DivisionSettingsFormSliceState<BasicSettingsFormValue> {
    playFieldsOptions: SalesforceListFieldResponse | undefined;
}

@Injectable()
export class DivisionBasicSettingsFormService {
    readonly #fb = inject(NonNullableFormBuilder);
    readonly #formLifecycleService = inject(FormLifecycleService);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #playService = inject(PlayService);
    readonly #dataMartService = inject(DataMartService);
    readonly #validators = inject(DivisionBasicSettingsFormValidatorsService);
    readonly #globalDivisionStore = inject(GlobalDivisionsStore);
    readonly #lifecyclesStore = inject(DataMartLifecyclesTableStore);

    public divisionBasicSettingsForm: FormGroup<DivisionBasicSettingsForm> = this.#fb.group({
        //
        dataMartTable: this.#fb.control('', Validators.required),
        dataMartRequiredField: this.#fb.control('', Validators.required),
        allowOverrideQueries: this.#fb.control(false, Validators.required),
        //
        multipleRecommendations: this.#fb.group({
            enabled: this.#fb.control<boolean>(false),
            maxRecommendations: this.#fb.control<number>(1, [
                conditionalValidator(
                    () => this.divisionBasicSettingsForm.controls.multipleRecommendations.controls.enabled.value,
                    Validators.required
                ),
                Validators.pattern(/^[1-9][0-9]*$/), // any whole number except 0
            ]),
            rankingField: this.#fb.control<string>(
                '',
                conditionalValidator(
                    () => this.divisionBasicSettingsForm.controls.multipleRecommendations.controls.enabled.value,
                    Validators.required
                )
            ),
        }),
        //
        lifecycleRulePlayRecordIdField: this.#fb.control('', Validators.required),
        lifecycleRuleRecommendationIdField: this.#fb.control('', Validators.required),
        //
        eloquaSiteName: this.#fb.control(''),
        //
        additionalFields: this.#fb.array<FormGroup<AdditionalFieldForm>>([]),
    });

    private initialFormState: DivisionBasicSettingsFormSliceState = {
        value: this.divisionBasicSettingsForm.getRawValue(),
        status: 'PENDING',
        loaded: false,
        dataMartTableOptions: [],
        salesforceObjectOptions: [],
        recommendationFieldsOptions: undefined,
        playFieldsOptions: undefined,
        selectedDivisionConfig: undefined,
    };

    private selectedDivisionConfig$ = toObservable<DivisionConfig | undefined>(
        this.#globalLoggedInUserStore.selectedDivisionConfig
    );
    private lifecycleRulesTable = computed<DataMartTable[] | undefined>(() => this.#lifecyclesStore.tables());
    private loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;

    /**
     * @description Wait until save to actual delete these
     */
    private fieldsWithAdditionalPlaysToDelete = signal<{ playId: number; additionalFieldId: string }[]>([]);

    /**
     * @description Track IDs of deleted additional fields so that the data mappings can remove rows using them
     */
    private additionalFieldsDeletedForFieldMappingDeletion = signal<string[]>([]);

    public divisionBasicSettingsFormState = signalSlice({
        initialState: this.initialFormState,
        sources: [
            // As the form changes
            formCombined(this.divisionBasicSettingsForm),
            // Useful for other config state on save
            this.selectedDivisionConfig$.pipe(map(config => ({ selectedDivisionConfig: config }))),
            // On load/changes to the division
            state =>
                this.#formLifecycleService.initializeAndPatchForm(
                    state.selectedDivisionConfig$,
                    state.loaded$,
                    this.divisionBasicSettingsForm,
                    data => this.initializeEmptyMapperControls(data),
                    data => this.mapDivisionConfigToFormData(data)
                ),
            // Data for options retrieved on load
            this.#dataMartService.getDataModel().pipe(
                map(dataModel => ({
                    dataMartTableOptions: dataModel.tables,
                }))
            ),
        ],
        selectors: state => ({
            dataMartFields: () =>
                state.dataMartTableOptions().find(table => table.name === state.value().dataMartTable)?.fields ?? [],
            dataMartTable: () => state.dataMartTableOptions().find(table => table.name === state.value().dataMartTable),
        }),
    });

    public submit() {
        const state = this.divisionBasicSettingsFormState;

        const divisionId = this.#globalLoggedInUserStore.selectedDivisionConfig()?.id;
        const divisionMapping = state().selectedDivisionConfig?.config?.mapping;
        const formValue = state().value;
        const datamartTable = this.divisionBasicSettingsFormState.dataMartTable();
        const lifecycleRulesTable = this.lifecycleRulesTable()?.at(0);
        const loggedInUserId = this.loggedInUserId();

        let submitted: Observable<{ submitted: boolean }>;

        if (divisionId && datamartTable && lifecycleRulesTable && loggedInUserId) {
            const uniqueSalesforceTargetFields: SalesforceUniqueFieldTarget[] =
                divisionMapping?.uniqueSalesforceTargetFields ?? [];

            const relationsAndCriteriaTypes$ = this.#formLifecycleService.getRelationsAndCrtieriaTypes(
                uniqueSalesforceTargetFields.length,
                uniqueSalesforceTargetFields.map(field => field.sourceField.name),
                formValue.dataMartTable,
                (sourceTableName, sourceColumnName) =>
                    this.#dataMartService.getRelations(sourceTableName, sourceColumnName)
            );

            const multipleRecommendationsTableMap$ = formValue.multipleRecommendations.enabled
                ? this.#dataMartService.getMultipleRecommendationsTableMap()
                : of(null);

            submitted = forkJoin([relationsAndCriteriaTypes$, multipleRecommendationsTableMap$]).pipe(
                switchMap(([relationsAndCriteriaTypes, multiRecTableMap]) =>
                    combineLatest([of(relationsAndCriteriaTypes), of(multiRecTableMap), this.removeAdditionalFields()])
                ),
                switchMap(([relationsAndCriteriaTypes, multiRecTableMap, _test]) => {
                    this.fieldsWithAdditionalPlaysToDelete.set([]);
                    this.additionalFieldsDeletedForFieldMappingDeletion.set([]);

                    const divisionConfig: RequiredDivisionConfigData = {
                        id: divisionId,
                        updatedBy: loggedInUserId,
                    };

                    const divisionBasicSettings: DivisionBasicSettings = this.formatMappingForSave(
                        formValue,
                        datamartTable,
                        lifecycleRulesTable,
                        relationsAndCriteriaTypes.criteriaTypes,
                        multiRecTableMap
                    );

                    const divisionConfigData = {
                        ...divisionMapping, // present during saves when other sections data exist
                        ...divisionBasicSettings, // will always be present, particularly first time
                        fieldMappings: this.removeDeletedAdditionalFieldsFromFieldMappings(),
                    };

                    return from(
                        this.#globalDivisionStore.saveDivisionConfigAndDataBasicSettings({
                            config: divisionConfig,
                            data: {
                                mapping: divisionConfigData,
                            },
                        })
                    ).pipe(map(() => ({ submitted: true })));
                })
            );
        } else {
            submitted = of({
                submitted: false,
            });
        }
        return lastValueFrom(submitted);
    }

    constructor() {
        effect(
            () => {
                this.#formLifecycleService.enableOrDisableWhenLoading(
                    this.divisionBasicSettingsFormState.loaded(),
                    this.divisionBasicSettingsForm
                );
            },
            { debugName: 'form init + load' }
        );
    }

    public setAdditonalFieldsToDelete(playsWithAdditionalFieldIds: { playId: number; additionalFieldId: string }[]) {
        this.fieldsWithAdditionalPlaysToDelete.update(plays => [...plays, ...playsWithAdditionalFieldIds]);
    }

    public setAdditionalFieldDeletedForFieldMappingDeletion(id: string) {
        this.additionalFieldsDeletedForFieldMappingDeletion.update(fields => [...fields, id]);
    }

    public getPlaysDependentOnAdditionalField(id: string) {
        const search: PlaySearch = {
            divisions: [this.#globalLoggedInUserStore.selectedIamDivision()!.divisionCode!],
            deleted: 'False',
            configSearch: [
                {
                    property: 'definition.additionalPlayFields',
                    operator: 'like',
                    value: id,
                },
            ],
        };
        return this.#playService.search(search);
    }

    public addHelperText(text: string, index: number) {
        this.divisionBasicSettingsForm.controls.additionalFields.controls
            .at(index)
            ?.controls.helperText.patchValue(text);
    }

    private removeDeletedAdditionalFieldsFromFieldMappings() {
        const fieldsDeleted = this.additionalFieldsDeletedForFieldMappingDeletion();
        const fieldMappings =
            this.divisionBasicSettingsFormState.selectedDivisionConfig()?.config?.mapping?.fieldMappings ?? [];

        const filtered = fieldMappings.filter(
            mapping =>
                !(
                    mapping.mappingRowType === 'definedField' &&
                    fieldsDeleted.includes((mapping.source as DefinedField).id)
                )
        );

        return filtered;
    }

    private removeAdditionalFields() {
        const additionalFieldIds = this.fieldsWithAdditionalPlaysToDelete().map(addtl => addtl.additionalFieldId);
        if (additionalFieldIds.length === 0) {
            return of([]);
        }
        return from(additionalFieldIds).pipe(
            concatMap(id =>
                this.#playService.removeAdditionalField(
                    id,
                    this.#globalLoggedInUserStore.selectedIamDivision()!.divisionCode!
                )
            ),
            toArray()
        );
    }

    /**
     * @description Step #1) Create empty form controls for X mapping fields of whatever type
     */
    private initializeEmptyMapperControls(divisionConfig: DivisionConfig) {
        const additionalFields = this.formatConfigMappingData(divisionConfig);

        function generateAdditionalFieldsMappings(
            additionalFieldsForm: FormArray<FormGroup<AdditionalFieldForm>>,
            fb: NonNullableFormBuilder,
            validators: DivisionBasicSettingsFormValidatorsService
        ): void {
            const additionalFieldsData = {
                length: additionalFields?.length ?? 0,
                ids: additionalFields.map(f => f.id),
                picklistElements: additionalFields?.map(f => f.picklistElements ?? []) ?? [],
            };
            for (let i = 0; i < additionalFieldsData.length; i++) {
                additionalFieldsForm.push(
                    fb.group({
                        id: fb.control<string>(additionalFieldsData.ids?.[i]),
                        source: fb.control<'Static' | 'User'>('Static'),
                        name: fb.control('', {
                            validators: [
                                Validators.required,
                                validators.nameTakenValidator(additionalFieldsData.ids?.[i] ?? ''),
                            ],
                        }),
                        type: fb.control<'varchar' | 'date' | 'boolean' | 'picklist'>('varchar'),
                        value: fb.control<string | undefined>('', {
                            validators: validators.additionFieldValueRequiredValidator(),
                        }),
                        required: fb.control(false),
                        picklistElements: fb.array<FormGroup<PicklistForm>>([]),
                        helperText: fb.control(''),
                    })
                );
                const picklistElements = additionalFieldsData.picklistElements[i];
                for (let j = 0; j < picklistElements.length; j++) {
                    additionalFieldsForm.at(i).controls.picklistElements?.push(
                        fb.group({
                            id: fb.control<string>(''),
                            name: fb.control<string>(''),
                            value: fb.control<string>(''),
                        })
                    );
                }
            }
        }

        generateAdditionalFieldsMappings(
            this.divisionBasicSettingsForm.controls.additionalFields,
            this.#fb,
            this.#validators
        );
    }

    /**
     * @description Step #1 and #2 helper
     */
    private formatConfigMappingData(
        divisionConfig: DivisionConfig
    ): DivisionBasicSettingsFormSliceState['value']['additionalFields'] {
        const additFieldsIncompletePicklistElements = divisionConfig?.config?.mapping?.additionalFields ?? [];
        const additionalFields: Required<DefinedField>[] = additFieldsIncompletePicklistElements.map(additField => {
            return {
                ...additField,
                // Optional fields must default to a defined fallback for the sake of the form
                picklistElements: additField.picklistElements ?? [],
                helperText: additField.helperText ?? '',
            };
        });
        return additionalFields;
    }

    /**
     * @description Step #2) Shape data to form slice state, and mark `loaded: true`
     */
    private mapDivisionConfigToFormData(divisionConfig: DivisionConfig): {
        value: Partial<BasicSettingsFormValue>;
        status: FormControlStatus;
        loaded: boolean;
    } {
        const dataMapping: DivisionMappingSettings | undefined = divisionConfig?.config?.mapping;
        const additionalFields = this.formatConfigMappingData(divisionConfig);

        const value: Partial<DivisionBasicSettingsFormSliceState['value']> = {
            dataMartTable: dataMapping?.dataMartTable,
            dataMartRequiredField: dataMapping?.dataMartRequiredField?.name,
            allowOverrideQueries: dataMapping?.allowOverrideQueries ?? false,
            multipleRecommendations: {
                enabled: dataMapping?.multipleRecommendations?.enabled ?? false,
                maxRecommendations: dataMapping?.multipleRecommendations?.maxRecommendations ?? 1,
                rankingField: dataMapping?.multipleRecommendations?.rankingField ?? '',
            },
            additionalFields: additionalFields,
            lifecycleRulePlayRecordIdField: dataMapping?.lifecycleRulePlayRecordIdField?.name,
            lifecycleRuleRecommendationIdField: dataMapping?.lifecycleRuleRecommendationIdField?.name,
            eloquaSiteName: dataMapping?.eloquaSiteName,
        };
        const statusAndLoading: Pick<DivisionBasicSettingsFormSliceState, 'status' | 'loaded'> = {
            status: this.divisionBasicSettingsForm.status,
            loaded: true,
        };
        return {
            value: value,
            status: statusAndLoading.status,
            loaded: statusAndLoading.loaded,
        };
    }

    /**
     * @description Maps over all the form data, derives respective server state, while
     *                  splitting up "both" fieldMapping into separate rows
     */
    private formatMappingForSave(
        formValue: DivisionBasicSettingsFormSliceState['value'],
        dataMartTable: DataMartTable,
        lifecycleRulesTable: DataMartTable,
        segmentationCriteriaType: CriteriaType[],
        multiRecTableMapping: DataMartMultipleRecommendationsTableMapResponse | null
    ): DivisionBasicSettings {
        const requiredField = dataMartTable.fields.find(f => f.name === formValue.dataMartRequiredField);

        const lifecycleRuleRecommendationIdField = (): DataMartField => {
            const currentField = lifecycleRulesTable.fields.find(
                f => f.name === formValue.lifecycleRuleRecommendationIdField
            );

            // tableName is not included in the data itself, so must be appended here.
            return { ...currentField!, tableName: lifecycleRulesTable.name };
        };

        const lifecycleRulePlayRecordIdField = (): DataMartField => {
            const currentField = lifecycleRulesTable.fields.find(
                f => f.name === formValue.lifecycleRulePlayRecordIdField
            );

            // tableName is not included in the data itself, so must be appended here.
            return { ...currentField!, tableName: lifecycleRulesTable.name };
        };

        const multiRecEnabled = formValue.multipleRecommendations?.enabled;

        return {
            dataMartTable: dataMartTable.name ?? '',
            dataMartRequiredField: requiredField!,
            allowOverrideQueries: formValue.allowOverrideQueries,
            additionalFields: formValue.additionalFields.map(field => {
                return { ...field, id: field.id ?? '' };
            }),
            lifecycleRulePlayRecordIdField: lifecycleRulePlayRecordIdField(),
            lifecycleRuleRecommendationIdField: lifecycleRuleRecommendationIdField(),
            eloquaSiteName: formValue.eloquaSiteName,
            allowedCriteria: segmentationCriteriaType,
            multipleRecommendations: {
                enabled: multiRecEnabled ?? false,
                rankingField: multiRecEnabled ? (formValue.multipleRecommendations?.rankingField ?? '') : '',
                tableMap: multiRecEnabled ? (multiRecTableMapping?.mrTableMap ?? []) : [],
                ...(multiRecEnabled && {
                    maxRecommendations: multiRecEnabled
                        ? (formValue.multipleRecommendations?.maxRecommendations ?? 0)
                        : 0,
                }),
            },
        };
    }
}
